import { memo } from 'react';
import { FiPause, FiPlay, FiX } from 'react-icons/fi';
import { FORECAST_LAYER_IDS, TILE_OVERLAY_LAYER_IDS, WEATHER_LAYER_CONFIGS } from '../../config/weatherLayers';
import { useShallow } from 'zustand/react/shallow';
import { LayerKey, useWeatherStore } from '../../store/weatherStore';
import { useModelLayers } from './hooks/useModelLayers';
import { useTranslation } from '../../i18n/LanguageContext';
import { useLayerLabels } from '../../i18n/useLayerLabels';
import { useModelLabels } from '../../i18n/useModelLabels';

// بلا props — memo يمنع إعادة الرسم مع كل تغيّر إطار زمني في NewWeatherMap (يشترك في متجره الخاص فقط).
export const LayerSidebar = memo(function LayerSidebar() {
    const {
        visibleLayers,
        setActiveLayer,
        isPlaying,
        setIsPlaying,
        selectedModel,
        setSelectedModel,
        sidebarOpen,
        setSidebarOpen,
        availableModels,
    } = useWeatherStore(useShallow((s) => ({ visibleLayers: s.visibleLayers, setActiveLayer: s.setActiveLayer, isPlaying: s.isPlaying, setIsPlaying: s.setIsPlaying, selectedModel: s.selectedModel, setSelectedModel: s.setSelectedModel, sidebarOpen: s.sidebarOpen, setSidebarOpen: s.setSidebarOpen, availableModels: s.availableModels })));

    const { t } = useTranslation();
    const { layerName } = useLayerLabels();
    const { modelResolution } = useModelLabels();
    const { status: modelStatus, layers: availableLayers } = useModelLayers(selectedModel);

    if (!sidebarOpen) return null;

    // طبقات التوقّع: فقط ما ولّده النموذج المختار (من meta.json). البلاطات والمتابعة مستقلّة.
    const forecastLayers = FORECAST_LAYER_IDS
        .filter((id) => availableLayers.has(id))
        .map((id) => WEATHER_LAYER_CONFIGS[id]);
    const tileOverlays = TILE_OVERLAY_LAYER_IDS.map((id) => WEATHER_LAYER_CONFIGS[id]);

    return (
        <>
            <div className="fixed inset-0 bg-black/50 z-[1100] animate-fade-in" onClick={() => setSidebarOpen(false)} />
            <div className="fixed top-0 start-0 w-[300px] max-[768px]:w-[min(320px,86vw)] h-screen bg-[var(--panel-bg)] backdrop-blur-[20px] border-e border-[var(--panel-border)] text-[var(--text-primary)] z-[1110] flex flex-col animate-slide-in-right">
                <div className="px-4 py-[18px] border-b border-[var(--divider)] flex justify-between items-center">
                    <span className="text-[17px] font-bold">{t('layers.sidebarTitle')}</span>
                    <button
                        className="w-8 h-8 flex items-center justify-center bg-[var(--hover-bg)] light:bg-[var(--stat-bg)] border-0 rounded-lg text-inherit light:text-[var(--text-secondary)] cursor-pointer text-base transition-all duration-200 hover:bg-[rgba(255,80,80,0.15)] hover:text-[#ff5050]"
                        onClick={() => setSidebarOpen(false)}
                        title={t('common.close')}
                    >
                        <FiX />
                    </button>
                </div>

                <div className="px-3 py-[14px] border-b border-[var(--divider)] flex flex-col gap-2">
                    {availableModels.map((model) => {
                        const isActive = selectedModel === model.id;
                        return (
                            <button
                                key={model.id}
                                className={`flex items-center gap-[10px] px-[14px] py-[10px] border-[1.5px] rounded-[10px] text-start text-sm font-semibold cursor-pointer transition-all duration-200 ${isActive
                                    ? 'bg-[rgba(102,126,234,0.12)] border-[var(--color-primary)]'
                                    : 'bg-white/[0.04] light:bg-[var(--stat-bg)] border-transparent hover:bg-[var(--hover-bg)] light:hover:bg-[var(--row-hover)]'
                                    }`}
                                onClick={() => setSelectedModel(model.id)}
                            >
                                <span
                                    className={`w-[10px] h-[10px] rounded-full shrink-0 animate-pulse-soft ${isActive ? 'bg-[var(--color-primary)] shadow-[0_0_8px_rgba(102,126,234,0.5)]' : 'bg-white/20'
                                        }`}
                                />
                                <span>{model.name} — {modelResolution(model)}</span>
                            </button>
                        );
                    })}
                </div>

                <div className="flex-1 overflow-y-auto px-2 py-[10px]">
                    {modelStatus === 'missing' && (
                        <div className="mx-1 mt-1 mb-[10px] px-3 py-[10px] text-xs leading-[1.6] text-white/[0.82] bg-[rgba(109,94,252,0.14)] border border-[rgba(109,94,252,0.35)] rounded-[10px] text-center">
                            {t('layers.modelPendingLong')}
                        </div>
                    )}
                    <LayerGroup title={t('layers.forecastGroup')} layers={forecastLayers} layerName={layerName} visibleLayers={visibleLayers} onSelect={setActiveLayer} />
                    <div className="h-px bg-[var(--divider)] my-2" />
                    <LayerGroup title={t('layers.tileGroup')} layers={tileOverlays} layerName={layerName} visibleLayers={visibleLayers} onSelect={setActiveLayer} />
                </div>

                <div className="px-3 py-[14px] border-t border-[var(--divider)] flex gap-[10px]">
                    <button
                        className="flex-1 flex items-center justify-center gap-2 p-3 bg-[linear-gradient(135deg,#667eea_0%,#764ba2_100%)] border-0 rounded-[10px] text-white text-sm font-semibold cursor-pointer transition-transform duration-200 hover:scale-[1.02]"
                        onClick={() => setIsPlaying(!isPlaying)}
                    >
                        {isPlaying ? <FiPause /> : <FiPlay />}
                        <span>{t(isPlaying ? 'common.pause' : 'common.play')}</span>
                    </button>
                </div>
            </div>
        </>
    );
});

function LayerGroup({ title, layers, visibleLayers, onSelect, layerName }: {
    title: string;
    layerName: (id: LayerKey) => string;
    layers: Array<(typeof WEATHER_LAYER_CONFIGS)[LayerKey]>;
    visibleLayers: Record<LayerKey, boolean>;
    onSelect: (layer: LayerKey) => void;
}) {
    return (
        <>
            <div className="text-[10px] font-bold uppercase tracking-[0.8px] opacity-45 light:text-[var(--text-muted)] px-3 pt-2 pb-[6px]">{title}</div>
            {layers.map((layer) => {
                const Icon = layer.icon;
                const isActive = visibleLayers[layer.id];
                return (
                    <button
                        key={layer.id}
                        className={`w-full flex items-center gap-3 px-3 py-[11px] mb-0.5 border-[1.5px] rounded-[10px] cursor-pointer transition-all duration-200 text-inherit text-start text-sm ${isActive
                            ? 'bg-[rgba(102,126,234,0.08)] border-[rgba(102,126,234,0.3)] light:border-[rgba(102,126,234,0.22)]'
                            : 'bg-transparent border-transparent hover:bg-[var(--hover-bg)] light:hover:bg-[var(--row-hover)]'
                            }`}
                        onClick={() => onSelect(layer.id)}
                    >
                        <div className={`w-9 h-9 flex items-center justify-center rounded-lg text-lg shrink-0 ${isActive ? 'bg-[rgba(102,126,234,0.15)]' : 'bg-white/5 light:bg-[var(--stat-bg)]'
                            }`}>
                            <Icon style={{ color: layer.color }} />
                        </div>
                        <span className="flex-1 font-medium">{layerName(layer.id)}</span>
                        {isActive && <div className="w-2 h-2 rounded-full shrink-0" style={{ background: layer.color }} />}
                    </button>
                );
            })}
        </>
    );
}
